/**
 * Drawer principal pour l'import WordPress
 * Gère toutes les étapes : sélection type → contenus → confirmation → import
 */
import type { WPContentType, WPContentItem, WPQuota, WPEstimate, WPImportStatus, ImportStep } from './types'
import type { Collection } from '../types'
import { useAppLanguage } from '../../../i18n'

// Utilitaire d'interpolation pour traductions avec variables
const interpolate = (template: string, vars: Record<string, string | number>) => {
  return Object.entries(vars).reduce(
    (acc, [key, val]) => acc.replace(`{${key}}`, String(val)),
    template
  )
}

interface WordPressImportDrawerProps {
  // État
  isOpen: boolean
  step: ImportStep
  selectedType: WPContentType | null
  selectedItems: WPContentItem[]
  
  // Données
  contentTypes: WPContentType[]
  contents: WPContentItem[]
  collections: Collection[]
  quota: WPQuota | null
  estimate: WPEstimate | null
  importStatus: WPImportStatus | null
  total: number
  hasMore: boolean
  
  // UI
  loading: boolean
  loadingContents: boolean
  error: string | null
  
  // Actions
  onClose: () => void
  onSelectType: (type: WPContentType) => void
  onToggleItem: (item: WPContentItem) => void
  onSelectAll: () => void
  onSelectNone: () => void
  onLoadMore: () => void
  onConfirm: () => void
  onBackToSelection: () => void
  onBackToTypes: () => void
  onStartImport: () => void
  onCancelImport: () => void
  
  // Setters
  targetCollectionId: number | null
  autoCreateCollections: boolean
  onSetTargetCollection: (id: number | null) => void
  onSetAutoCreateCollections: (value: boolean) => void
}

export default function WordPressImportDrawer({
  isOpen,
  step,
  selectedType,
  selectedItems,
  contentTypes,
  contents,
  collections,
  quota,
  estimate,
  importStatus,
  total,
  hasMore,
  loading,
  loadingContents,
  error,
  onClose,
  onSelectType,
  onToggleItem,
  onSelectAll,
  onSelectNone,
  onLoadMore,
  onConfirm,
  onBackToSelection,
  onBackToTypes,
  onStartImport,
  onCancelImport,
  targetCollectionId,
  autoCreateCollections,
  onSetTargetCollection,
  onSetAutoCreateCollections,
}: WordPressImportDrawerProps) {
  const { t, language } = useAppLanguage()
  const locale = language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : 'fr-FR'
  const pluralSuffix = (n: number) => (n > 1 ? 's' : '')
  
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
        onClick={step === 'importing' ? undefined : onClose}
      />
      
      {/* Drawer */}
      <div className="absolute inset-y-0 right-0 w-full max-w-2xl bg-white/95 shadow-2xl dark:bg-zinc-950/95 animate-slide-in-right overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-zinc-200/70 px-6 py-4 dark:border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/40">
              <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                {t.knowledges.wpImport.title}
              </h2>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                {step === 'type_selection' && t.knowledges.wpImport.chooseContentType}
                {step === 'content_selection' && `${t.knowledges.wpImport.selectContentsPrefix} ${(selectedType?.label.toLowerCase() || t.knowledges.wpImport.items)}`}
                {step === 'confirmation' && t.knowledges.wpImport.confirmImport}
                {step === 'importing' && t.knowledges.wpImport.importing}
                {step === 'completed' && t.knowledges.wpImport.completed}
                {step === 'error' && t.knowledges.wpImport.error}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {step !== 'importing' && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Quota bar */}
        {quota && step !== 'importing' && step !== 'completed' && (
          <div className="border-b border-zinc-200/70 px-6 py-3 dark:border-zinc-800">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-600 dark:text-zinc-300">{t.knowledges.wpImport.availableQuota}</span>
              <span className="font-medium text-zinc-900 dark:text-white">
                {quota.available_credits.toFixed(1)} / {quota.limit_credits} {t.knowledges.wpImport.credits}
              </span>
            </div>
            <div className="mt-2 h-2 rounded-full bg-zinc-200 dark:bg-zinc-700 overflow-hidden">
              <div 
                className="h-full rounded-full bg-emerald-500 transition-all"
                style={{ width: `${Math.min(100, quota.usage_percent)}%` }}
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Erreur globale */}
          {error && (
            <div className="m-6 rounded-2xl border border-red-200/70 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
              {error}
            </div>
          )}

          {/* Loading */}
          {loading && step === 'type_selection' && (
            <div className="flex items-center justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
            </div>
          )}

          {/* Étape 1: Sélection du type */}
          {step === 'type_selection' && !loading && (
            <TypeSelectionStep 
              contentTypes={contentTypes} 
              onSelect={onSelectType}
            />
          )}

          {/* Étape 2: Sélection des contenus */}
          {step === 'content_selection' && (
            <ContentSelectionStep
              contents={contents}
              selectedItems={selectedItems}
              selectedType={selectedType}
              total={total}
              hasMore={hasMore}
              loading={loadingContents}
              onToggleItem={onToggleItem}
              onSelectAll={onSelectAll}
              onSelectNone={onSelectNone}
              onLoadMore={onLoadMore}
              onBack={onBackToTypes}
            />
          )}

          {/* Étape 3: Confirmation */}
          {step === 'confirmation' && estimate && (
            <ConfirmationStep
              selectedItems={selectedItems}
              estimate={estimate}
              collections={collections}
              targetCollectionId={targetCollectionId}
              autoCreateCollections={autoCreateCollections}
              onSetTargetCollection={onSetTargetCollection}
              onSetAutoCreateCollections={onSetAutoCreateCollections}
              onBack={onBackToSelection}
            />
          )}

          {/* Étape 4: Import en cours */}
          {step === 'importing' && importStatus && (
            <ImportProgressStep 
              status={importStatus}
              onCancel={onCancelImport}
            />
          )}

          {/* Étape 5: Terminé */}
          {step === 'completed' && importStatus && (
            <CompletedStep
              status={importStatus}
              onClose={onClose}
            />
          )}
        </div>

        {/* Footer */}
        {(step === 'content_selection' || step === 'confirmation') && (
          <div className="border-t border-zinc-200/70 px-6 py-4 dark:border-zinc-800">
            {step === 'content_selection' && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">
                  {selectedItems.length} {t.knowledges.wpImport.selected}{pluralSuffix(selectedItems.length)}
                  {estimate && (
                    <span className="ml-2 text-zinc-500">
                      ({estimate.credits_required.toFixed(2)} {t.knowledges.wpImport.credits})
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={selectedItems.length === 0}
                  className="rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t.knowledges.wpImport.continue}
                </button>
              </div>
            )}
            
            {step === 'confirmation' && estimate && (
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  {estimate.can_import ? (
                    <span className="text-emerald-600 dark:text-emerald-400">
                      {t.knowledges.wpImport.quotaEnough}
                    </span>
                  ) : (
                    <span className="text-red-600 dark:text-red-400">
                      {interpolate(t.knowledges.wpImport.quotaNotEnough, { n: estimate.items_over_quota })}
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={onBackToSelection}
                    className="rounded-full border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-100 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800"
                  >
                    {t.knowledges.wpImport.back}
                  </button>
                  <button
                    type="button"
                    onClick={onStartImport}
                    disabled={!estimate.can_import || loading}
                    className="rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading
                      ? t.knowledges.wpImport.launching
                      : interpolate(t.knowledges.wpImport.importN, { n: selectedItems.length, s: pluralSuffix(selectedItems.length) })}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Sous-composants
// ============================================================================

function TypeSelectionStep({ 
  contentTypes, 
  onSelect 
}: { 
  contentTypes: WPContentType[]
  onSelect: (type: WPContentType) => void 
}) {
  return (
    <div className="p-6">
      <div className="grid gap-3">
        {contentTypes.map(type => (
          <button
            key={type.type_id}
            type="button"
            onClick={() => type.available && type.count > 0 && onSelect(type)}
            disabled={!type.available || type.count === 0}
            className="flex items-center justify-between rounded-2xl border border-zinc-200/70 bg-white/80 p-4 text-left transition hover:border-emerald-300 hover:bg-emerald-50/50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-zinc-700 dark:bg-zinc-900/80 dark:hover:border-emerald-700 dark:hover:bg-emerald-900/20"
          >
            <div className="flex items-center gap-4">
              <span className="text-2xl">{type.icon}</span>
              <div>
                <div className="font-medium text-zinc-900 dark:text-white">{type.label}</div>
                <div className="text-sm text-zinc-500 dark:text-zinc-400">
                  {type.count} {type.count > 1 ? 'éléments' : 'élément'}
                  {!type.available && ' (non disponible)'}
                </div>
              </div>
            </div>
            <svg className="h-5 w-5 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  )
}

function ContentSelectionStep({
  contents,
  selectedItems,
  selectedType,
  total,
  hasMore,
  loading,
  onToggleItem,
  onSelectAll,
  onSelectNone,
  onLoadMore,
  onBack,
}: {
  contents: WPContentItem[]
  selectedItems: WPContentItem[]
  selectedType: WPContentType | null
  total: number
  hasMore: boolean
  loading: boolean
  onToggleItem: (item: WPContentItem) => void
  onSelectAll: () => void
  onSelectNone: () => void
  onLoadMore: () => void
  onBack: () => void
}) {
  const { t, language } = useAppLanguage()
  const locale = language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : 'fr-FR'
  const selectedIds = new Set(selectedItems.map(i => i.wp_id))

  return (
    <div className="p-6">
      {/* Header avec actions */}
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t.knowledges.wpImport.back}
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onSelectAll}
            className="text-sm text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
          >
            {t.knowledges.wpImport.selectAll}
          </button>
          <span className="text-zinc-300 dark:text-zinc-600">|</span>
          <button
            type="button"
            onClick={onSelectNone}
            className="text-sm text-zinc-500 hover:text-zinc-700 dark:text-zinc-400"
          >
            {t.knowledges.wpImport.none}
          </button>
        </div>
      </div>

      {/* Info total */}
      <p className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
        {total} {(selectedType?.label.toLowerCase() || t.knowledges.wpImport.items)} {t.knowledges.wpImport.availableSuffix}
      </p>

      {/* Liste */}
      <div className="space-y-2">
        {contents.map(item => {
          const isSelected = selectedIds.has(item.wp_id)
          return (
            <button
              key={item.wp_id}
              type="button"
              onClick={() => onToggleItem(item)}
              className={`w-full flex items-start gap-3 rounded-xl border p-3 text-left transition ${
                isSelected
                  ? 'border-emerald-300 bg-emerald-50/80 dark:border-emerald-700 dark:bg-emerald-900/30'
                  : 'border-zinc-200/70 bg-white/80 hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900/80 dark:hover:border-zinc-600'
              }`}
            >
              {/* Checkbox */}
              <div className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border ${
                isSelected
                  ? 'border-emerald-500 bg-emerald-500 text-white'
                  : 'border-zinc-300 dark:border-zinc-600'
              }`}>
                {isSelected && (
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-zinc-900 dark:text-white truncate">
                    {item.title || t.knowledges.wpImport.noTitle}
                  </span>
                  {item.is_imported && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      item.is_modified
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                        : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
                    }`}>
                      {item.is_modified ? 'Modifié' : 'Importé'}
                      {item.is_modified ? t.knowledges.wpImport.modified : t.knowledges.wpImport.imported}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex items-center gap-3 text-xs text-zinc-500 dark:text-zinc-400">
                  <span>
                    {item.char_count > 0 
                      ? interpolate(t.knowledges.wpImport.charCount, { n: item.char_count.toLocaleString(locale) })
                      : t.knowledges.wpImport.approxChars}
                  </span>
                  {item.parent_title && (
                    <span className="truncate">📁 {item.parent_title}</span>
                  )}
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Load more */}
      {hasMore && (
        <button
          type="button"
          onClick={onLoadMore}
          disabled={loading}
          className="mt-4 w-full rounded-xl border border-zinc-200/70 bg-white/80 py-3 text-sm font-medium text-zinc-600 transition hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-300 dark:hover:bg-zinc-800"
        >
          {loading ? t.common.loading : t.knowledges.wpImport.loadMore}
        </button>
      )}
    </div>
  )
}

function ConfirmationStep({
  selectedItems,
  estimate,
  collections,
  targetCollectionId,
  autoCreateCollections,
  onSetTargetCollection,
  onSetAutoCreateCollections,
  onBack,
}: {
  selectedItems: WPContentItem[]
  estimate: WPEstimate
  collections: Collection[]
  targetCollectionId: number | null
  autoCreateCollections: boolean
  onSetTargetCollection: (id: number | null) => void
  onSetAutoCreateCollections: (value: boolean) => void
  onBack: () => void
}) {
  return (
    <div className="p-6 space-y-6">
      {/* Retour */}
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Modifier la sélection
      </button>

      {/* Résumé */}
      <div className="rounded-2xl border border-emerald-200/70 bg-emerald-50/80 p-4 dark:border-emerald-900/50 dark:bg-emerald-900/20">
        <h3 className="font-semibold text-emerald-800 dark:text-emerald-200">Résumé de l'import</h3>
        <div className="mt-3 space-y-2 text-sm text-emerald-700 dark:text-emerald-300">
          <div className="flex justify-between">
            <span>Contenus sélectionnés</span>
            <span className="font-medium">{estimate.selected_count}</span>
          </div>
          <div className="flex justify-between">
            <span>Total caractères</span>
            <span className="font-medium">{estimate.total_chars.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span>Crédits requis</span>
            <span className="font-medium">{estimate.credits_required.toFixed(2)}</span>
          </div>
          <hr className="border-emerald-200 dark:border-emerald-800" />
          <div className="flex justify-between font-medium">
            <span>Crédits après import</span>
            <span>{(estimate.credits_available - estimate.credits_required).toFixed(2)} / {estimate.credits_limit}</span>
          </div>
        </div>
      </div>

      {/* Barre de quota */}
      <div className="rounded-2xl border border-zinc-200/70 bg-white/80 p-4 dark:border-zinc-700 dark:bg-zinc-900/80">
        <h3 className="font-semibold text-zinc-800 dark:text-zinc-200">Quota après import</h3>
        <div className="mt-3 h-3 rounded-full bg-zinc-200 dark:bg-zinc-700 overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all ${
              estimate.can_import ? 'bg-emerald-500' : 'bg-red-500'
            }`}
            style={{ 
              width: `${Math.min(100, ((estimate.credits_used + estimate.credits_required) / estimate.credits_limit) * 100)}%` 
            }}
          />
        </div>
        <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
          {estimate.can_import 
            ? '✅ Import possible' 
            : `⚠️ Quota dépassé de ${(estimate.credits_required - estimate.credits_available).toFixed(2)} crédits`}
        </p>
      </div>

      {/* Options */}
      <div className="rounded-2xl border border-zinc-200/70 bg-white/80 p-4 dark:border-zinc-700 dark:bg-zinc-900/80">
        <h3 className="font-semibold text-zinc-800 dark:text-zinc-200">Options d'import</h3>
        
        {/* Collection cible */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
            Collection de destination
          </label>
          <select
            value={targetCollectionId ?? ''}
            onChange={e => onSetTargetCollection(e.target.value ? Number(e.target.value) : null)}
            className="mt-1 w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white"
          >
            <option value="">Créer automatiquement</option>
            {collections.map(c => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </div>

        {/* Auto-create collections */}
        {!targetCollectionId && (
          <label className="mt-4 flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={autoCreateCollections}
              onChange={e => onSetAutoCreateCollections(e.target.checked)}
              className="h-4 w-4 rounded border-zinc-300 text-emerald-500 focus:ring-emerald-400 dark:border-zinc-600"
            />
            <span className="text-sm text-zinc-700 dark:text-zinc-300">
              Créer des collections depuis les cours WordPress
            </span>
          </label>
        )}
      </div>
    </div>
  )
}

function ImportProgressStep({
  status,
  onCancel,
}: {
  status: WPImportStatus
  onCancel: () => void
}) {
  return (
    <div className="p-6 space-y-6">
      {/* Progression principale */}
      <div className="text-center">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
        </div>
        <h3 className="mt-4 text-lg font-semibold text-zinc-900 dark:text-white">
          Import en cours...
        </h3>
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          {status.items_processed} / {status.items_total} contenus traités
        </p>
      </div>

      {/* Barre de progression */}
      <div>
        <div className="h-3 rounded-full bg-zinc-200 dark:bg-zinc-700 overflow-hidden">
          <div 
            className="h-full rounded-full bg-emerald-500 transition-all"
            style={{ width: `${status.progress}%` }}
          />
        </div>
        <div className="mt-2 flex justify-between text-xs text-zinc-500 dark:text-zinc-400">
          <span>{status.progress}%</span>
          <span>
            ✅ {status.items_success} réussis 
            {status.items_failed > 0 && ` • ❌ ${status.items_failed} échoués`}
          </span>
        </div>
      </div>

      {/* Item actuel */}
      {status.current_item && (
        <div className="rounded-xl border border-zinc-200/70 bg-zinc-50/80 p-3 dark:border-zinc-700 dark:bg-zinc-800/80">
          <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-300">
            <div className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
            <span className="truncate">{status.current_item}</span>
          </div>
        </div>
      )}

      {/* Bouton annuler */}
      <div className="pt-4 text-center">
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
        >
          Annuler l'import
        </button>
      </div>
    </div>
  )
}

function CompletedStep({
  status,
  onClose,
}: {
  status: WPImportStatus
  onClose: () => void
}) {
  const hasErrors = status.items_failed > 0

  return (
    <div className="p-6 space-y-6 text-center">
      {/* Icône */}
      <div className={`inline-flex h-16 w-16 items-center justify-center rounded-full ${
        hasErrors 
          ? 'bg-amber-100 dark:bg-amber-900/40' 
          : 'bg-emerald-100 dark:bg-emerald-900/40'
      }`}>
        {hasErrors ? (
          <svg className="h-8 w-8 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        ) : (
          <svg className="h-8 w-8 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>

      {/* Message */}
      <div>
        <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">
          {hasErrors ? 'Import terminé avec des erreurs' : 'Import terminé !'}
        </h3>
        <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
          {status.items_success} contenu{status.items_success > 1 ? 's' : ''} importé{status.items_success > 1 ? 's' : ''} avec succès
          {hasErrors && ` • ${status.items_failed} erreur${status.items_failed > 1 ? 's' : ''}`}
        </p>
      </div>

      {/* Statistiques */}
      <div className="mx-auto max-w-xs rounded-2xl border border-zinc-200/70 bg-white/80 p-4 dark:border-zinc-700 dark:bg-zinc-900/80">
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {status.items_success}
            </div>
            <div className="text-xs text-zinc-500 dark:text-zinc-400">Réussis</div>
          </div>
          <div>
            <div className={`text-2xl font-bold ${
              hasErrors ? 'text-red-600 dark:text-red-400' : 'text-zinc-400'
            }`}>
              {status.items_failed}
            </div>
            <div className="text-xs text-zinc-500 dark:text-zinc-400">Échoués</div>
          </div>
        </div>
      </div>

      {/* Bouton fermer */}
      <button
        type="button"
        onClick={onClose}
        className="rounded-full bg-emerald-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600"
      >
        Fermer
      </button>
    </div>
  )
}
